import React, { useEffect, useMemo, useState } from "react";
import {
  MdVpnKey,
  MdAdd,
  MdContentCopy,
  MdDelete,
  MdClose,
  MdWarningAmber,
  MdCheckCircle,
} from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../components/card";
import api from "../../../services/axios";
import { ApiKey, ApiKeyCreated } from "../../../interfaces/ApiKey";
import { createApiKey, getApiKeys, revokeApiKey } from "../../../services/integrationApi";
import { getModulosApi } from "../../../services/apiDocsApi";
import { ModuloInfo } from "../apiDocs/openapi";
import { mensagemDoServidor } from "../../../utils/mensagemDoServidor";

/*
  Chave de integração carrega PAPEL, não permissão — e continua assim de propósito. A chave não é
  uma pessoa: não tem perfil atribuído nem tela onde alguém o edite. O `PermissionResolver` traduz
  esses papéis pela mesma tabela de paridade dos perfis de sistema, então a chave nunca alcança
  mais do que o perfil equivalente alcançaria.

  "Client" e "Finance" ficam fora: nenhuma integração externa deve falar pelo cliente nem mexer em
  faturamento por chave.
*/
const ALL_ROLES = ["Admin", "Manager", "Employee"];

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copiado para a área de transferência.");
  } catch {
    toast.error("Não foi possível copiar.");
  }
};

// Base da API (sem o sufixo /api) — usada nas instruções de conexão do MCP/REST.
const apiBaseUrl = () => {
  const raw = (api.defaults.baseURL || window.location.origin).replace(/\/+$/, "");
  return raw.replace(/\/api$/i, "");
};

const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);

  // Formulário de criação
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<string[]>([...ALL_ROLES]);
  const [expiresInDays, setExpiresInDays] = useState<string>("");
  // Módulos da chave: nada marcado = todos os módulos.
  const [modulosDisponiveis, setModulosDisponiveis] = useState<ModuloInfo[]>([]);
  const [modulosSel, setModulosSel] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  // Chave recém-criada (mostrada uma única vez)
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);

  const load = () => {
    setLoading(true);
    getApiKeys()
      .then(setKeys)
      .catch((e) => {
        console.error("Erro ao carregar API keys", e);
        toast.error("Falha ao carregar as chaves.");
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  useEffect(() => {
    getModulosApi()
      .then(setModulosDisponiveis)
      .catch((e) => {
        console.error("Erro ao carregar módulos da API", e);
        toast.error("Não foi possível carregar os módulos. A chave só poderá ser criada com acesso a todos.");
      });
  }, []);

  const toggleModulo = (id: string) =>
    setModulosSel((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));

  const nomeDoModulo = (id: string) => modulosDisponiveis.find((m) => m.id === id)?.nome ?? id;

  const toggleRole = (role: string) =>
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));

  const resetForm = () => {
    setName("");
    setRoles([...ALL_ROLES]);
    setExpiresInDays("");
    setModulosSel([]);
    setFormOpen(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Informe um nome para a chave.");
      return;
    }
    if (roles.length === 0) {
      toast.error("Selecione ao menos um papel.");
      return;
    }
    setCreating(true);
    try {
      const result = await createApiKey({
        name: name.trim(),
        roles,
        modulos: modulosSel,
        expiresInDays: expiresInDays ? Number(expiresInDays) : null,
      });
      setCreated(result);
      resetForm();
      load();
      toast.success("Chave criada. Copie o valor agora — ele não será exibido novamente.");
    } catch (e: any) {
      console.error("Erro ao criar API key", e);
      toast.error(mensagemDoServidor(e, "Falha ao criar a chave."));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!window.confirm(`Revogar a chave "${key.name}"? Sistemas que a usam deixarão de funcionar.`)) return;
    try {
      await revokeApiKey(key.id);
      toast.success("Chave revogada.");
      load();
    } catch (e: any) {
      console.error("Erro ao revogar API key", e);
      toast.error(mensagemDoServidor(e, "Falha ao revogar a chave."));
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para a área de transferência.");
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

  const statusBadge = (k: ApiKey) => {
    if (k.revoked)
      return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[11px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">Revogada</span>;
    if (k.expiresAt && new Date(k.expiresAt) < new Date())
      return <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">Expirada</span>;
    return <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Ativa</span>;
  };

  const activeCount = useMemo(
    () => keys.filter((k) => !k.revoked && (!k.expiresAt || new Date(k.expiresAt) >= new Date())).length,
    [keys]
  );

  return (
    <div className="mt-5 flex animate-fade-up flex-col gap-4">
      {/* Cabeçalho */}
      <Card extra="overflow-hidden p-0">
        <div className="dv-aura relative overflow-hidden bg-dv-gradient px-5 py-5 text-white shadow-dv-glow">
          <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
          <div className="relative flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 backdrop-blur" aria-hidden="true">
                <MdVpnKey className="h-6 w-6" />
              </span>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-white ring-1 ring-white/20">
                  Integrações
                </span>
                <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-white">Chaves de API</h1>
                <p className="text-xs text-white/80">
                  Para integrar outros sistemas e o MCP (Claude Desktop) ao NINA. {activeCount} ativa(s).
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setFormOpen((v) => !v)}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-white px-4 py-2 text-sm font-bold text-ink-900 shadow-dv-md transition-all hover:bg-gray-100 active:scale-[.98]"
            >
              <MdAdd className="h-4 w-4" aria-hidden="true" /> Gerar nova chave
            </button>
          </div>
        </div>
      </Card>

      {/* Chave recém-criada (uma única vez) */}
      {created && (
        <Card extra="p-5 border border-emerald-200 dark:border-emerald-500/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300">
                <MdCheckCircle className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-navy-700 dark:text-white">Chave “{created.name}” criada</p>
                <p className="mb-2 flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                  <MdWarningAmber className="h-4 w-4 shrink-0" /> Copie agora — este valor não será exibido novamente.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="max-w-full overflow-x-auto rounded-lg bg-gray-100 px-3 py-2 font-mono text-xs text-navy-700 dark:bg-navy-900 dark:text-gray-200">
                    {created.apiKey}
                  </code>
                  <button
                    type="button"
                    onClick={() => copy(created.apiKey)}
                    className="flex items-center gap-1.5 rounded-lg bg-dv-blue-600 px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-dv-blue-700 active:scale-[.98]"
                  >
                    <MdContentCopy className="h-4 w-4" /> Copiar
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCreated(null)}
              aria-label="Fechar"
              className="rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-navy-700"
            >
              <MdClose className="h-5 w-5" />
            </button>
          </div>

          <ConnectionInfo apiKey={created.apiKey} />
        </Card>
      )}

      {/* Formulário de criação */}
      {formOpen && (
        <Card extra="p-5">
          <p className="mb-3 text-sm font-bold text-navy-700 dark:text-white">Nova chave de API</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">Nome</label>
              <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Claude Desktop MCP" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">Expira em (dias) — opcional</label>
              <input
                type="number"
                min={1}
                className={inputCls}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder="Em branco = não expira"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">Papéis concedidos</label>
              <div className="flex flex-wrap gap-2">
                {ALL_ROLES.map((r) => {
                  const active = roles.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r)}
                      className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-[.98] ${
                        active
                          ? "bg-dv-blue-600 text-white shadow-dv-md"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-700"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">
                Os papéis definem o que a chave pode fazer (mesma regra dos usuários). Padrão: todos.
              </p>
            </div>
            <div className="md:col-span-2">
              <span id="modulos-label" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Módulos da API
              </span>
              <div className="flex flex-wrap gap-2" role="group" aria-labelledby="modulos-label">
                {modulosDisponiveis.map((m) => {
                  const active = modulosSel.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleModulo(m.id)}
                      aria-pressed={active}
                      title={m.descricao}
                      className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 active:scale-[.98] ${
                        active
                          ? "bg-dv-blue-600 text-white shadow-dv-md"
                          : "bg-gray-50 text-gray-600 hover:bg-gray-100 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-700"
                      }`}
                    >
                      {m.nome}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">
                {modulosSel.length === 0
                  ? "Nenhum marcado: a chave alcança todos os módulos."
                  : `A chave só alcança: ${modulosSel.map(nomeDoModulo).join(", ")}. Fora deles a API responde 403.`}
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2.5">
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className="rounded-xl bg-ink-900 px-6 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
            >
              {creating ? "Gerando…" : "Gerar chave"}
            </button>
          </div>
        </Card>
      )}

      {/* Lista */}
      <Card extra="p-5">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>
        ) : keys.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">
            Nenhuma chave criada ainda. Use “Gerar nova chave” para começar.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-navy-900">
                  {["Nome", "Prefixo", "Papéis", "Módulos", "Status", "Último uso", "Expira", "Criada", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                    <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">{k.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-gray-300">{k.prefix}…</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{k.roles}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {(k.modulos ?? []).length === 0 ? "Todos" : k.modulos.map(nomeDoModulo).join(", ")}
                    </td>
                    <td className="px-4 py-3">{statusBadge(k)}</td>
                    <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{fmtDate(k.lastUsedAt)}</td>
                    <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{k.expiresAt ? fmtDate(k.expiresAt) : "Nunca"}</td>
                    <td className="px-4 py-3 tabular-nums text-gray-700 dark:text-gray-300">{fmtDate(k.dtCreated)}</td>
                    <td className="px-4 py-3 text-right">
                      {!k.revoked && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(k)}
                          title="Revogar"
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-50 active:scale-[.98] dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                        >
                          <MdDelete className="h-3.5 w-3.5" /> Revogar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ── Instruções de conexão (Claude Desktop + REST / qualquer IA) ──────────────

const CodeBlock: React.FC<{ title: string; code: string; hint?: string }> = ({ title, code, hint }) => (
  <div>
    <div className="mb-1 flex items-center justify-between gap-2">
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{title}</span>
      <button
        type="button"
        onClick={() => copyToClipboard(code)}
        className="flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-navy-900 dark:text-gray-200 dark:hover:bg-navy-700"
      >
        <MdContentCopy className="h-3.5 w-3.5" /> Copiar
      </button>
    </div>
    <pre className="overflow-x-auto rounded-lg bg-ink-900 p-3 text-[11px] leading-relaxed text-gray-100">
      <code>{code}</code>
    </pre>
    {hint && <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">{hint}</p>}
  </div>
);

const ConnectionInfo: React.FC<{ apiKey: string }> = ({ apiKey }) => {
  const base = apiBaseUrl();

  const claudeConfig = JSON.stringify(
    {
      mcpServers: {
        nina: {
          command: "node",
          args: ["<CAMINHO_DO_PROJETO>/nina-mcp/dist/index.js"],
          env: { NINA_API_BASE_URL: base, NINA_API_KEY: apiKey },
        },
      },
    },
    null,
    2
  );

  const restExample = `curl -H "X-Api-Key: ${apiKey}" ${base}/api/client/Clients`;

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-white/10">
      <p className="mb-3 text-sm font-bold text-navy-700 dark:text-white">Como conectar</p>

      <div className="flex flex-col gap-4">
        {/* Resumo dos valores */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <CodeBlock title="NINA_API_BASE_URL" code={base} />
          <CodeBlock title="NINA_API_KEY" code={apiKey} />
        </div>

        {/* Claude Desktop */}
        <CodeBlock
          title="Claude Desktop — claude_desktop_config.json"
          code={claudeConfig}
          hint="Settings → Developer → Edit Config. Troque <CAMINHO_DO_PROJETO> pelo caminho real da pasta nina-mcp e reinicie o Claude Desktop. Antes: dentro de nina-mcp rode `npm install && npm run build`."
        />

        {/* REST / qualquer IA */}
        <CodeBlock
          title="Qualquer sistema/IA (REST) — header X-Api-Key"
          code={restExample}
          hint={`Qualquer provedor (OpenAI, Gemini, etc.) ou sistema pode chamar a API REST diretamente enviando o header X-Api-Key. A lista de endpoints está na documentação da API; peça à DataValue.`}
        />
      </div>
    </div>
  );
};

export default ApiKeysPage;
